import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, Mail, Lock, User, Eye, EyeOff, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button, Input } from '../../components/common';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordRequirements = [
    { label: 'At least 8 characters', met: formData.password.length >= 8 },
    { label: 'Contains a number', met: /\d/.test(formData.password) },
    { label: 'Contains uppercase letter', met: /[A-Z]/.test(formData.password) },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!passwordRequirements.every((r) => r.met)) {
      setError('Password does not meet requirements');
      return;
    }

    setLoading(true);

    try {
      await register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-1 bg-bg-secondary border-r border-border items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h2 className="text-3xl font-bold text-text-primary mb-4">
            Start your copy trading journey
          </h2>
          <p className="text-text-secondary mb-8">
            Join thousands of traders who are already copying their way to profits. Start with as little as Rs10,000.
          </p>
          <div className="space-y-4 text-left">
            <div className="flex items-start gap-3 p-4 bg-bg-tertiary rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-profit/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-profit" />
              </div>
              <div>
                <div className="font-medium text-text-primary">Verified Performance</div>
                <div className="text-sm text-text-muted">All stats computed from real exchange data</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-bg-tertiary rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-profit/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-profit" />
              </div>
              <div>
                <div className="font-medium text-text-primary">Smart Position Sizing</div>
                <div className="text-sm text-text-muted">Automatic scaling based on your capital</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-bg-tertiary rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-profit/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-profit" />
              </div>
              <div>
                <div className="font-medium text-text-primary">Multi-Exchange Support</div>
                <div className="text-sm text-text-muted">Delta, Bybit, CoinDCX, CoinSwitch</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-accent-primary flex items-center justify-center">
              <Copy className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-text-primary tracking-tight">MIMIQ</span>
          </Link>

          <h1 className="text-2xl font-bold text-text-primary mb-2">Create your account</h1>
          <p className="text-text-secondary mb-8">Get started with copy trading today</p>

          {error && (
            <div className="mb-6 p-4 bg-loss/10 border border-loss/20 rounded-lg text-loss text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Username"
              type="text"
              name="username"
              placeholder="Choose a username"
              value={formData.username}
              onChange={handleChange}
              leftIcon={<User className="w-4 h-4" />}
              required
            />

            <Input
              label="Email"
              type="email"
              name="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              leftIcon={<Mail className="w-4 h-4" />}
              required
            />

            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Create a password"
              value={formData.password}
              onChange={handleChange}
              leftIcon={<Lock className="w-4 h-4" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-text-muted hover:text-text-secondary"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
              required
            />

            {formData.password && (
              <div className="space-y-2">
                {passwordRequirements.map((req, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${req.met ? 'bg-profit' : 'bg-bg-tertiary'}`}>
                      {req.met && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <span className={req.met ? 'text-text-secondary' : 'text-text-muted'}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <Input
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              leftIcon={<Lock className="w-4 h-4" />}
              error={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'Passwords do not match' : ''}
              required
            />

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                required
                className="w-4 h-4 mt-0.5 rounded border-border bg-bg-tertiary text-accent-primary focus:ring-accent-primary/20"
              />
              <span className="text-sm text-text-secondary">
                I agree to the{' '}
                <Link to="/terms" className="text-accent-primary hover:text-accent-hover">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-accent-primary hover:text-accent-hover">
                  Privacy Policy
                </Link>
              </span>
            </div>

            <Button
              type="submit"
              loading={loading}
              className="w-full"
              size="lg"
            >
              Create Account
            </Button>
          </form>

          <p className="mt-8 text-center text-text-secondary">
            Already have an account?{' '}
            <Link to="/login" className="text-accent-primary hover:text-accent-hover font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
